import { ReactNode, useRef, useState } from 'react';

import { types } from '@boss/boss-client';
import { useGeoLocation } from '@boss/hooks';
import { SearchDropdown } from '@boss/ui';
import { Coordinates, calculateDistance } from '@boss/utils';

import { sortPickupPoints } from './sort-pickup-points';

type Props = {
  stores: types.Store[];
  favouriteShopId?: string;
  value?: string | null;
  onChange: (store: types.Store) => void;
  searchLabel?: string;
  selectLabel?: string;
  favouriteLabel?: string;
  distanceLocale?: string;
  variant?: 'basket' | 'basketB2c';
  className?: string;
  dropdownClassName?: string;
  error?: boolean;
  renderStoreStatus?: (store: types.Store) => ReactNode;
  /** When true, fetch geolocation eagerly on mount (not lazily on first open). */
  earlyGeolocation?: boolean;
};

type Option = { value: string; label: string };
type MutableRef<T> = { current: T };
type DistanceCoordinates = Coordinates | null;

const valueOr = <T,>(value: T | null | undefined, fallback: T): T => value ?? fallback;

const formatDistance = (
  store: types.Store,
  coordinates: DistanceCoordinates,
  distanceLocale: string,
): string | undefined => {
  if (!coordinates || !Number.isFinite(store.latitude) || !Number.isFinite(store.longitude)) {
    return undefined;
  }

  const distance = calculateDistance(store.latitude, store.longitude, coordinates.latitude, coordinates.longitude);

  return new Intl.NumberFormat(distanceLocale, { maximumFractionDigits: 1 }).format(distance);
};

const basketOptionLabel = (
  b2b: boolean,
  store: types.Store,
  label: string,
  distance: string | undefined,
  renderStoreStatus: (store: types.Store) => ReactNode,
) => (
  <span className="flex w-full min-w-0 items-center gap-2">
    {renderStoreStatus(store)}
    <span className={b2b ? 'text-gray-dark min-w-0 truncate text-[13.5px]' : 'min-w-0 truncate'}>{label}</span>
    {distance !== undefined && (
      <span className="text-orderImport-step-description-text ml-auto shrink-0 text-xs">{distance} km</span>
    )}
  </span>
);

const plainOptionLabel = (store: types.Store, label: string, renderStoreStatus: (store: types.Store) => ReactNode) => (
  <>
    {renderStoreStatus(store)}
    {label}
  </>
);

const createOptionRenderer = (
  storesById: Map<string, types.Store>,
  coordinates: DistanceCoordinates,
  distanceLocale: string,
  basketStyle: boolean,
  basketB2bStyle: boolean,
  renderStoreStatus: ((store: types.Store) => ReactNode) | undefined,
) => {
  if (!renderStoreStatus) {
    return undefined;
  }

  const statusRenderer = renderStoreStatus;

  return (option: Option): ReactNode => {
    const store = storesById.get(option.value);

    if (!store) {
      return option.label;
    }

    const distance = formatDistance(store, coordinates, distanceLocale);

    return basketStyle
      ? basketOptionLabel(basketB2bStyle, store, option.label, distance, statusRenderer)
      : plainOptionLabel(store, option.label, statusRenderer);
  };
};

const resolveSelectedLabel = (
  selectedStore: types.Store | undefined,
  basketStyle: boolean,
  selectLabel: string | undefined,
  favouriteShopId: string | undefined,
  favouriteLabel: string,
  renderStoreStatus: ((store: types.Store) => ReactNode) | undefined,
): ReactNode => {
  if (!selectedStore) {
    return basketStyle ? <span className="text-base">{selectLabel}</span> : selectLabel;
  }

  return (
    <span className="flex min-w-0 items-center gap-2">
      {renderStoreStatus?.(selectedStore)}
      <span className="text-gray-dark min-w-0 truncate text-base font-bold">{selectedStore.name}</span>
      {selectedStore.id === favouriteShopId && (
        <span className="text-orderImport-step-description-text shrink-0 text-xs font-normal">· {favouriteLabel}</span>
      )}
    </span>
  );
};

const createStoreChangeHandler = (storesById: Map<string, types.Store>, onChange: (store: types.Store) => void) => {
  return (storeId: string): void => {
    const store = storesById.get(storeId);

    if (store) {
      onChange(store);
    }
  };
};

const runEagerGeolocation = (enabled: boolean | undefined, fetchCoordinates: () => void): void => {
  if (enabled) {
    fetchCoordinates();
  }
};

const requestGeolocation = async (
  requestedRef: MutableRef<boolean>,
  fetchGeolocation: () => Promise<Coordinates | undefined>,
  setCoordinates: (coordinates: Coordinates) => void,
): Promise<void> => {
  if (requestedRef.current) {
    return;
  }

  requestedRef.current = true;

  const resolvedCoordinates = await fetchGeolocation();

  if (resolvedCoordinates) {
    setCoordinates(resolvedCoordinates);
  }
};

const BASKET_B2B_CLASSES =
  'max-w-[320px] w-full min-w-0 [&:has(>div+div)>div:first-child]:border-green-dark [&>div+div>ul]:flex [&>div+div>ul]:flex-col [&>div+div>ul]:gap-0 [&>div+div>ul>li:last-child]:border-b-0 [&>div+div>ul>li]:border-b [&>div+div>ul>li]:border-gray-200 [&>div+div>ul>li]:border-x-0 [&>div+div>ul>li]:border-t-0 [&>div+div>ul>li]:px-3 [&>div+div>ul>li]:py-[9px] [&>div+div>ul>li]:rounded-none [&>div+div>ul>li]:text-s [&>div+div]:w-full [&>div+div]:rounded-[6px] [&>div+div]:border-gray-200 [&>div+div]:shadow-soft';
const BASKET_B2C_CLASSES =
  'max-w-[320px] w-full min-w-0 [&>div+div>ul>li]:rounded-[6px] [&>div+div>ul>li]:border [&>div+div>ul>li]:border-gray-200 [&>div+div>ul>li]:px-3 [&>div+div>ul>li]:py-[9px] [&>div+div>ul]:flex [&>div+div>ul]:flex-col [&>div+div>ul]:gap-2 [&>div+div]:w-full [&>div+div]:rounded-[6px] [&>div+div]:border-gray-200';

const basketWrapperClassName = (b2b: boolean, className: string | undefined): string =>
  b2b ? `${BASKET_B2B_CLASSES} ${className ?? ''}` : `${BASKET_B2C_CLASSES} ${className ?? ''}`;

const basketDropdownClassName = (dropdownClassName: string | undefined): string =>
  `w-full min-w-0 rounded-[6px] border-gray-200 bg-white px-3 py-[7px] text-gray text-sm ${dropdownClassName ?? ''}`;

/**
 * Shared pickup-point dropdown: pins the favourite shop first, sorts the rest via
 * the CL-1 cascade (`sortPickupPoints`) and emits the full selected `types.Store`
 * so consumers get both the preferred shop id and the `coloradepotid` warehouse
 * code. Geolocation is requested lazily on the first open — never on mount, so
 * customers on default "Levering" are not prompted.
 */
const PickupPointDropdown = (props: Props) => {
  const { fetchGeolocation } = useGeoLocation();
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const geolocationRequested = useRef(false);

  const fetchAndSetCoordinates = () => requestGeolocation(geolocationRequested, fetchGeolocation, setCoordinates);

  // Eager geolocation: start fetching as soon as the dropdown renders (before the user opens it).
  // This is used in the basket where Pickup might be newly selected and we want coordinates
  // ready by the time the user expands the list (Issue 1).
  runEagerGeolocation(props.earlyGeolocation, fetchAndSetCoordinates);

  const storesById = new Map(props.stores.map(store => [store.id, store]));
  const basketStyle = props.variant !== undefined;
  const basketB2bStyle = props.variant === 'basket';
  const selectedStore = props.value ? storesById.get(props.value) : undefined;
  const favouriteLabel = valueOr(props.favouriteLabel, 'favoriet');
  const distanceLocale = valueOr(props.distanceLocale, 'en');
  const options = sortPickupPoints(props.stores, {
    favouriteShopId: props.favouriteShopId,
    coordinates,
  }).map(store => ({ value: store.id, label: store.name }));
  const renderOptionLabel = createOptionRenderer(
    storesById,
    coordinates,
    distanceLocale,
    basketStyle,
    basketB2bStyle,
    props.renderStoreStatus,
  );
  const handleChange = createStoreChangeHandler(storesById, props.onChange);
  const selectedLabel = resolveSelectedLabel(
    selectedStore,
    basketStyle,
    props.selectLabel,
    props.favouriteShopId,
    favouriteLabel,
    props.renderStoreStatus,
  );

  return (
    <SearchDropdown
      className={basketStyle ? basketWrapperClassName(basketB2bStyle, props.className) : props.className}
      dropdownClassName={basketStyle ? basketDropdownClassName(props.dropdownClassName) : props.dropdownClassName}
      error={props.error}
      label={basketStyle ? selectedLabel : undefined}
      onChange={handleChange}
      onOpen={fetchAndSetCoordinates}
      options={options}
      renderOptionLabel={renderOptionLabel}
      searchLabel={props.searchLabel}
      selectLabel={props.selectLabel}
      value={basketStyle ? undefined : props.value}
    />
  );
};

export default PickupPointDropdown;
